import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../services/categoryService';
import './CreateCategory.css';

const CreateCategory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    kicker: '',
    card_title: '',
    card_description: '',
    button_text: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Category name is required');
      }

      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();
      
      // Add text fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('kicker', formData.kicker || '');
      formDataToSend.append('card_title', formData.card_title || '');
      formDataToSend.append('card_description', formData.card_description || '');
      formDataToSend.append('button_text', formData.button_text || '');

      // Add image if provided
      if (image) {
        formDataToSend.append('image', image);
      }

      await categoryService.createCategory(formDataToSend);
      navigate('/categories', { state: { message: 'Category created successfully!' } });
    } catch (err) {
      let errorMessage = 'Failed to create category';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-category-container">
      <div className="page-header">
        <h1 className="page-title">Create Category</h1>
        <button
          onClick={() => navigate('/categories')}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="category-form">
          <div className="form-group">
            <label htmlFor="name">
              Category Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter category name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter category description (optional)"
            />
          </div>

          {/* Homepage card text — shown when this category is featured on the home page */}
          <fieldset className="form-group" style={{ border: '1px solid #e5e5e5', padding: '16px', borderRadius: '4px' }}>
            <legend style={{ padding: '0 8px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              Homepage Card (when featured)
            </legend>

            <div className="form-group">
              <label htmlFor="kicker">Kicker</label>
              <input
                type="text"
                id="kicker"
                name="kicker"
                value={formData.kicker}
                onChange={handleChange}
                placeholder="e.g. ESSENTIALS"
              />
            </div>

            <div className="form-group">
              <label htmlFor="card_title">Card Title</label>
              <input
                type="text"
                id="card_title"
                name="card_title"
                value={formData.card_title}
                onChange={handleChange}
                placeholder={formData.name || 'Overrides the category name on the card'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="card_description">Card Description</label>
              <textarea
                id="card_description"
                name="card_description"
                value={formData.card_description}
                onChange={handleChange}
                rows="2"
                placeholder="e.g. Refined accessories for every wrist."
              />
            </div>

            <div className="form-group">
              <label htmlFor="button_text">Button Text</label>
              <input
                type="text"
                id="button_text"
                name="button_text"
                value={formData.button_text}
                onChange={handleChange}
                placeholder="e.g. Explore Collection"
              />
            </div>
          </fieldset>

          {/* Image Upload */}
          <div className="form-group">
            <label htmlFor="image">Category Image (Optional)</label>
            {imagePreview ? (
              <div className="image-preview-container">
                <img
                  src={imagePreview}
                  alt="Category preview"
                  className="image-preview"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="btn-remove-image"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="file-upload-area">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
                <label htmlFor="image" className="file-upload-label">
                  <span className="upload-icon">📷</span>
                  <span>Click to upload category image</span>
                  <span className="file-hint">JPG, PNG, WEBP (Max 5MB)</span>
                </label>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="error-message-bottom">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/categories')}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCategory;
